import cn from 'classnames'
import { cloneElement, type ReactElement, type ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { CockpitScopeChip } from '../../../components/cockpitScopeChip'
import { useCockpitScope } from '../../hooks/useCockpitScope'

import styles from './CockpitScopeValues.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

export type TCockpitScopeValuesTourTargets = Record<
  'scopePeriod' | 'scopeMetric' | 'scopeCurrency',
  string
>

interface ICockpitScopeValuesProps {
  tourTargets?: TCockpitScopeValuesTourTargets
  /** `fill` stretches the pills across the row edge to edge; `fit` keeps each one as wide as its
   *  longest value, for a row they share with other controls; `track` sets the values as blue
   *  segments of one grey track, each under its caption, so they read as one group. */
  layout?: 'fill' | 'fit' | 'track'
}

interface IScopeValueProps {
  label: ReactNode
  children: ReactNode
  /** `track` only: the value's column in the shared track. */
  column?: number
}

const ScopeValue = ({ label, children, column }: IScopeValueProps) => {
  /* In the track the value lends its caption and its segment to the track's grid columns. */
  const cell = column ? { gridColumn: column } : undefined

  return (
    <div className={styles.value}>
      <span className={styles.caption} style={cell}>
        {label}
      </span>
      {column ? (
        <span className={styles.cell} style={cell}>
          {children}
        </span>
      ) : (
        children
      )}
    </div>
  )
}

/** Period, metric and currency, each a blue pill under its caption opening its own list. */
export const CockpitScopeValues = ({ tourTargets, layout = 'fill' }: ICockpitScopeValuesProps) => {
  const scope = useCockpitScope()
  const isTrack = layout === 'track'
  const appearance = isTrack ? 'segmentOnly' : 'pill'

  const values = [
    <ScopeValue key="period" label={<Translation text={KEYS.period.label} span />}>
      <CockpitScopeChip
        appearance={appearance}
        value={scope.periodLabel}
        label={<Translation text={KEYS.period.label} span />}
        ariaLabel={KEYS.period.label}
        options={scope.periodOptions}
        sizeValues={scope.periodSizeLabels}
        selectedKeys={scope.selectedPeriods}
        onSelect={scope.selectPeriod}
        closeOnSelect={false}
        tourId={tourTargets?.scopePeriod}
      />
    </ScopeValue>,
    scope.metric && (
      <ScopeValue key="metric" label={<Translation text={KEYS.metrics.label} span />}>
        <CockpitScopeChip
          appearance={appearance}
          value={scope.metric}
          label={<Translation text={KEYS.metrics.label} span />}
          ariaLabel={KEYS.metrics.label}
          options={scope.metricOptions}
          sizeValues={scope.metricSizeLabels}
          selectedKeys={[scope.metric]}
          onSelect={scope.selectMetric}
          tourId={tourTargets?.scopeMetric}
        />
      </ScopeValue>
    ),
    scope.currency && (
      <ScopeValue key="currency" label={<Translation text={KEYS.currency.label} span />}>
        <CockpitScopeChip
          appearance={appearance}
          value={scope.currency}
          label={<Translation text={KEYS.currency.label} span />}
          ariaLabel={KEYS.currency.label}
          options={scope.currencyOptions}
          sizeValues={scope.currencySizeLabels}
          selectedKeys={[scope.currency]}
          onSelect={scope.selectCurrency}
          disabled={scope.isCurrencyDisabled}
          disabledTooltip={<Translation text={KEYS.currency.multipleLocalCurrenciesDisabled} />}
          tourId={tourTargets?.scopeCurrency}
        />
      </ScopeValue>
    ),
  ].filter(Boolean) as ReactElement<IScopeValueProps>[]

  if (isTrack) {
    return (
      <div className={cn(styles.root, styles.track)}>
        <span className={styles.trackFill} aria-hidden="true" />
        {values.map((value, index) => cloneElement(value, { column: index + 1 }))}
      </div>
    )
  }

  return <div className={cn(styles.root, styles[layout])}>{values}</div>
}

CockpitScopeValues.displayName = 'CockpitScopeValues'
